import React, { useEffect, useRef, useState } from 'react';
import { getPrebuiltEditor, collectionInitPromise, isInitDone, sharedCollection } from '../blocksuite-init.js';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import * as store from '@blocksuite/affine/store';
import '../../../../style.css';
import '../../../../apps/_common/components/starter-debug-menu.js';
import '../../../../apps/_common/components/properties-panel.js';
import '../../../../apps/_common/components/custom-adapter-panel.js';
import '../../../../apps/_common/components/custom-frame-panel.js';
import '../../../../apps/_common/components/custom-outline-panel.js';
import '../../../../apps/_common/components/custom-outline-viewer.js';
import '../../../../apps/_common/components/left-side-panel.js';
import '../../../../apps/_common/components/docs-panel.js';

interface DesignStudioEditorProps {
  mode: 'page' | 'edgeless';
  docId?: string;
}

// ─── GLOBAL OWNERSHIP REGISTRY ───────────────────────────────────────────────
// The prebuilt editor is a single DOM element (singleton). Only ONE container
// can host it at a time. We track ownership by integer ID so that:
//   - When a new component mounts it can claim ownership (move the editor).
//   - When a component unmounts it fires a 'reclaim' event so another live
//     component can immediately re-claim the editor without any flash.
let _nextId = 0;
let _activeOwnerId = -1;

// Lightweight event bus for reclaim signals
type ReclaimCallback = () => void;
const _reclaimListeners = new Map<number, ReclaimCallback>();

function registerReclaim(id: number, cb: ReclaimCallback) {
  _reclaimListeners.set(id, cb);
}
function unregisterReclaim(id: number) {
  _reclaimListeners.delete(id);
}
function broadcastReclaim(excludeId: number) {
  // Notify ALL other live instances to try re-claiming.
  // The first one that responds wins.
  for (const [id, cb] of _reclaimListeners) {
    if (id !== excludeId) cb();
  }
}

function setupPanels(container: HTMLDivElement, editor: any) {
  let propertiesPanel = container.querySelector('properties-panel') as any;
  if (!propertiesPanel) {
    propertiesPanel = document.createElement('properties-panel');
    propertiesPanel.editor = editor;
    container.appendChild(propertiesPanel);
  }
  let docsPanel = container.querySelector('.docs-panel-dummy') as any;
  if (!docsPanel) {
    docsPanel = document.createElement('div');
    docsPanel.className = 'docs-panel-dummy';
    docsPanel.style.display = 'none';
    docsPanel.editor = editor;
    container.appendChild(docsPanel);
  }
  let framePanel = container.querySelector('custom-frame-panel') as any;
  if (!framePanel) {
    framePanel = document.createElement('custom-frame-panel');
    framePanel.editor = editor;
    container.appendChild(framePanel);
  }
  let outlinePanel = container.querySelector('custom-outline-panel') as any;
  if (!outlinePanel) {
    outlinePanel = document.createElement('custom-outline-panel');
    outlinePanel.editor = editor;
    container.appendChild(outlinePanel);
  }
  let outlineViewer = container.querySelector('custom-outline-viewer') as any;
  if (!outlineViewer) {
    outlineViewer = document.createElement('custom-outline-viewer');
    outlineViewer.editor = editor;
    outlineViewer.toggleOutlinePanel = () => { outlinePanel.toggleDisplay(); };
    container.appendChild(outlineViewer);
  }
  let leftSidePanel = container.querySelector('left-side-panel') as any;
  if (!leftSidePanel) {
    leftSidePanel = document.createElement('left-side-panel');
    container.appendChild(leftSidePanel);
  }
  let adapterPanel = container.querySelector('custom-adapter-panel') as any;
  if (!adapterPanel) {
    adapterPanel = document.createElement('custom-adapter-panel');
    adapterPanel.editor = editor;
    container.appendChild(adapterPanel);
  }
  let debugMenu = container.querySelector('starter-debug-menu') as any;
  if (!debugMenu) {
    debugMenu = document.createElement('starter-debug-menu');
    debugMenu.collection = sharedCollection;
    debugMenu.editor = editor;
    debugMenu.propertiesPanel = propertiesPanel;
    debugMenu.docsPanel = docsPanel;
    debugMenu.framePanel = framePanel;
    debugMenu.outlinePanel = outlinePanel;
    debugMenu.outlineViewer = outlineViewer;
    debugMenu.leftSidePanel = leftSidePanel;
    debugMenu.adapterPanel = adapterPanel;
    container.appendChild(debugMenu);
  }
  return debugMenu;
}

function removePanels(container: HTMLDivElement | null) {
  if (!container) return;
  [
    'starter-debug-menu', 'properties-panel', 'docs-panel', '.docs-panel-dummy',
    'custom-frame-panel', 'custom-outline-panel', 'custom-outline-viewer',
    'left-side-panel', 'custom-adapter-panel'
  ].forEach(sel => {
    const el = container.querySelector(sel);
    if (el) try { el.remove(); } catch (_) {}
  });
}

function resolveTargetDoc(editorMode: 'page' | 'edgeless', customDocId?: string) {
  let targetDocId = customDocId || (editorMode === 'edgeless' ? 'doc:canvas' : 'doc:home');
  let targetDoc = sharedCollection.getDoc(targetDocId);
  if (!targetDoc) {
    if (targetDocId.startsWith('doc:notebook-')) {
      targetDoc = sharedCollection.createDoc(targetDocId);
      const docStore = targetDoc.getStore();
      docStore.load();
      const pageBlockId = docStore.addBlock('affine:page', { title: new store.Text('') });
      const noteBlockId = docStore.addBlock('affine:note', {}, pageBlockId);
      docStore.addBlock('affine:paragraph', { text: new store.Text('Start typing notebook ideas here...') }, noteBlockId);
    } else {
      targetDoc = sharedCollection.docs.values().next().value;
    }
  }
  return targetDoc;
}

function applyEditorState(
  editor: any,
  container: HTMLDivElement,
  mode: 'page' | 'edgeless',
  docId: string | undefined,
  debugMenuRef: React.MutableRefObject<any>
) {
  const targetDoc = resolveTargetDoc(mode, docId);
  if (targetDoc) {
    const docStore = targetDoc.getStore();
    docStore.load();
    if (editor.doc !== docStore) editor.doc = docStore;
  }
  if (editor.std?.provider) {
    try {
      const svc = editor.std.provider.get(DocModeProvider);
      if (svc) svc.setPrimaryMode(mode, editor.doc.id);
    } catch (_) {}
  }
  editor.mode = mode;
  if (mode === 'edgeless') {
    if (!debugMenuRef.current) debugMenuRef.current = setupPanels(container, editor);
  } else {
    if (debugMenuRef.current) { removePanels(container); debugMenuRef.current = null; }
  }
}

export function DesignStudioEditor({ mode, docId }: DesignStudioEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const debugMenuRef = useRef<any>(null);
  const myId = useRef<number>(++_nextId).current;
  const [loading, setLoading] = useState(!isInitDone() || !getPrebuiltEditor());

  // Latest values accessible inside async callbacks / event handlers
  const modeRef = useRef(mode);
  const docIdRef = useRef(docId);
  modeRef.current = mode;
  docIdRef.current = docId;

  // ─── CLAIM OWNERSHIP FUNCTION ─────────────────────────────────────────────
  // Idempotent: safe to call multiple times. Moves the editor into our
  // container (auto-detaches from previous parent — no blank flash ever).
  const claim = React.useCallback(async (triggeredByReclaim = false) => {
    if (!isInitDone() || !getPrebuiltEditor()) {
      await collectionInitPromise;
    }
    if (!containerRef.current) return;

    const editor = getPrebuiltEditor();
    if (!editor) return;

    // Configure the editor for our mode/doc BEFORE moving the DOM element
    applyEditorState(editor, containerRef.current, modeRef.current, docIdRef.current, debugMenuRef);

    // Move editor into our container (no-op if already here)
    if (!containerRef.current.contains(editor)) {
      containerRef.current.appendChild(editor);
    }

    _activeOwnerId = myId;
    if (!triggeredByReclaim) setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId]);

  // ─── MOUNT / DOCID EFFECT ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    // Register as a reclaim listener so other instances can wake us up
    registerReclaim(myId, () => {
      if (!cancelled && containerRef.current) {
        claim(true).catch(console.error);
      }
    });

    // Claim editor on first mount
    claim(false).catch(console.error);

    return () => {
      cancelled = true;
      unregisterReclaim(myId);

      // If we currently own the editor, release ownership and broadcast
      // so that another live instance immediately re-claims it.
      if (_activeOwnerId === myId) {
        _activeOwnerId = -1;
        removePanels(containerRef.current);
        debugMenuRef.current = null;
        // Do NOT remove the editor element from the DOM — just leave it in
        // place and let the next owner move it. This is the key to zero-flash.
        broadcastReclaim(myId);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // ─── IN-PLACE MODE SWITCH ─────────────────────────────────────────────────
  // Only runs when this instance already owns the editor.
  useEffect(() => {
    if (loading) return;
    if (_activeOwnerId !== myId) return;
    if (!containerRef.current) return;
    const editor = getPrebuiltEditor();
    if (!editor) return;
    try {
      applyEditorState(editor, containerRef.current, mode, docId, debugMenuRef);
    } catch (err) {
      console.error('DesignStudioEditor mode switch error:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, docId, loading]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: '#f8f9fa', fontFamily: 'system-ui, sans-serif', gap: 12, zIndex: 10,
        }}>
          <div style={{
            width: 36, height: 36, border: '3px solid #e2e8f0',
            borderTop: '3px solid #6366f1', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {mode === 'edgeless' ? 'Loading Canvas\u2026' : 'Loading Docs\u2026'}
          </span>
        </div>
      )}
      <style>{`
        starter-debug-menu .debug-menu {
          position: absolute !important; top: 0px !important; left: 0px !important;
          width: auto !important; pointer-events: auto !important;
          z-index: 1000 !important; display: flex !important; overflow: visible !important;
        }
        starter-debug-menu .default-toolbar {
          margin: 0 !important; border-radius: 0px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
          background: #ffffff !important; border: none !important;
        }
        starter-debug-menu sl-button::part(base) {
          height: 37px !important; font-size: 13px !important;
          display: inline-flex !important; align-items: center !important; justify-content: center;
        }
        starter-debug-menu sl-select::part(combobox) { height: 37px !important; }
      `}</style>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
